import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@vtak/shared';
import { fetchJson } from '@/services/api-client';

/** Real satellite profile from SatNOGS (via the server proxy), when available. */
export interface SatelliteProfile {
  image: string;
  operator: string;
  status: string;
  launched: string;
  country: string;
  website: string;
}

/**
 * Fetch a satellite's SatNOGS profile (image + operator/status/launch/country)
 * by NORAD id, cached forever per id (immutable). Returns null while loading,
 * for non-satellites (no id), or when SatNOGS has no entry — callers fall back
 * to the icon + computed orbit data.
 */
export function useSatelliteProfile(noradId: number | undefined): SatelliteProfile | null {
  const { data } = useQuery({
    queryKey: queryKeys.space.satelliteProfile(noradId ?? -1),
    enabled: noradId !== undefined,
    staleTime: Infinity,
    queryFn: () => fetchSatelliteProfile(noradId as number),
  });
  return data ?? null;
}

/** Fetch one satellite's profile from the server proxy, or null. */
async function fetchSatelliteProfile(noradId: number): Promise<SatelliteProfile | null> {
  return fetchJson<SatelliteProfile | null>(`/api/space/satellite/${noradId}`, null);
}
